import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TeacherSections.css';

const TeacherSectionDashboard = () => {
  const { t, i18n } = useTranslation('teacher');
  
  // Format date based on current language
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
  const [teacherStats, setTeacherStats] = useState({
    students: 156,
    courses: 8,
    liveClasses: 3,
    earnings: 2450,
    articles: 12,
    latestReview: 4.8
  });

  const [reviews, setReviews] = useState([
    {
      id: 1,
      studentName: 'Sarah Johnson',
      rating: 5,
      commentKey: 'excellentTeacher',
      date: '2024-03-12',
      course: 'Advanced English Grammar'
    },
    {
      id: 2,
      studentName: 'Mike Chen',
      rating: 5,
      commentKey: 'greatClass',
      date: '2024-03-10',
      course: 'Business English Speaking'
    },
    {
      id: 3,
      studentName: 'Anna Lopez',
      rating: 4,
      commentKey: 'helpfulTeacher',
      date: '2024-03-08',
      course: 'IELTS Preparation'
    }
  ]);

  const generateStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`star ${i <= rating ? 'filled' : ''}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="teacher-main-content">
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('dashboard.welcome', { name: 'Ms. Smith' })}</h1>
        <p className="teacher-content-subtitle">{t('dashboard.subtitle')}</p>
      </div>

      {/* Dashboard Overview Cards */}
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">dashboard</i>
            {t('dashboard.teachingOverview')}
          </div>
        </div>
        
        <div className="teacher-overview-grid">
          <div className="teacher-overview-card">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('dashboard.students')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">people</i>
              </div>
            </div>
            <div className="teacher-overview-value">{teacherStats.students}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +12 {t('dashboard.thisMonth')}
            </div>
          </div>

          <div className="teacher-overview-card success">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('dashboard.coursesUploaded')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">menu_book</i>
              </div>
            </div>
            <div className="teacher-overview-value">{teacherStats.courses}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +2 {t('dashboard.thisMonth')}
            </div>
          </div>

          <div className="teacher-overview-card warning">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('dashboard.upcomingLiveClasses')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">event</i>
              </div>
            </div>
            <div className="teacher-overview-value">{teacherStats.liveClasses}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">schedule</i>
              {t('dashboard.todayTomorrow')}
            </div>
          </div>

          <div className="teacher-overview-card accent">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('dashboard.earningsThisMonth')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">attach_money</i>
              </div>
            </div>
            <div className="teacher-overview-value">${teacherStats.earnings}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +15% {t('dashboard.fromLastMonth')}
            </div>
          </div>

          <div className="teacher-overview-card info">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('dashboard.articlesPublished')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">edit</i>
              </div>
            </div>
            <div className="teacher-overview-value">{teacherStats.articles}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              +3 {t('dashboard.thisMonth')}
            </div>
          </div>

          <div className="teacher-overview-card success">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('dashboard.latestReview')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">star</i>
              </div>
            </div>
            <div className="teacher-overview-value">{teacherStats.latestReview}/5</div>
            <div className="teacher-overview-change">
              <i className="material-icons">star</i>
              {t('dashboard.excellentRating')}
            </div>
          </div>
        </div>
      </div>

      {/* Latest Reviews */}
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">rate_review</i>
            {t('dashboard.latestReviews')}
          </div>
        </div>
        
        <div className="teacher-reviews-grid">
          {reviews.map((review) => (
            <div key={review.id} className="teacher-review-card">
              <div className="review-header">
                <div className="review-student">
                  <div className="student-avatar">
                    {review.studentName.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="review-info">
                    <h4 className="student-name">{review.studentName}</h4>
                    <p className="course-name">{review.course}</p>
                  </div>
                </div>
                <div className="review-rating">
                  {generateStars(review.rating)}
                  <span className="rating-text">{review.rating}/5</span>
                </div>
              </div>
              <div className="review-content">
                <p className="review-comment">"{t(`dashboard.reviews.${review.commentKey}`)}"</p>
                <span className="review-date">{formatDate(review.date)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">flash_on</i>
            {t('dashboard.quickActions')}
          </div>
        </div>
        
        <div className="teacher-actions-grid">
          <button className="teacher-action-card" onClick={() => console.log('Add Course')}>
            <div className="action-icon">
              <i className="material-icons">add_circle</i>
            </div>
            <div className="action-content">
              <h4>{t('dashboard.addNewCourse')}</h4>
              <p>{t('dashboard.addNewCourseDesc')}</p>
            </div>
          </button>

          <button className="teacher-action-card" onClick={() => console.log('Schedule Class')}>
            <div className="action-icon">
              <i className="material-icons">video_call</i>
            </div>
            <div className="action-content">
              <h4>{t('dashboard.scheduleLiveClass')}</h4>
              <p>{t('dashboard.scheduleLiveClassDesc')}</p>
            </div>
          </button>

          <button className="teacher-action-card" onClick={() => console.log('Add Article')}>
            <div className="action-icon">
              <i className="material-icons">edit</i>
            </div>
            <div className="action-content">
              <h4>{t('dashboard.writeArticle')}</h4>
              <p>{t('dashboard.writeArticleDesc')}</p>
            </div>
          </button>

          <button className="teacher-action-card" onClick={() => console.log('View Earnings')}>
            <div className="action-icon">
              <i className="material-icons">receipt_long</i>
            </div>
            <div className="action-content">
              <h4>{t('dashboard.viewEarnings')}</h4>
              <p>{t('dashboard.viewEarningsDesc')}</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherSectionDashboard; 